import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button (opens HTML/CSS/JS Editor)
function TryItButton({ code }) {
  const handleClick = () => {
    // Send HTML + JS content as one string to /editorHTML (where HTMLCSSJavaScriptCodeEditorTerminal runs)
    const encodedCode = encodeURIComponent(
      JSON.stringify({
        html: code,
        css: "",
        js: "",
      })
    );
    window.open(`/editorHTML?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_And_HTML_Events() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎯 JavaScript Events — Complete Guide</h1>

      {/* 💡 Intro Section */}
      <div
        style={{
          backgroundColor: "#dbeafe",
          padding: "1.5rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          lineHeight: "1.7",
          fontSize: "1.15rem",
        }}
      >
        <p>
          A <b>JavaScript event</b> is an action or occurrence in the browser —
          such as a user clicking, typing, submitting a form, or resizing the
          window. JavaScript can <b>react to these events</b> using event
          handlers.
        </p>
      </div>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
        }}
      >
        <h2 style={{ color: "#15803d", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <ul className="tutorial-list-points">
          <li>Events allow interaction between user and webpage.</li>
          <li>
            Use <code>addEventListener()</code> for cleaner separation of HTML
            and JS.
          </li>
          <li>
            Common events include click, input, submit, mouseover, keydown, etc.
          </li>
          <li>
            Always call <code>event.preventDefault()</code> when handling forms.
          </li>
        </ul>
      </div>

      {/* Example 1: Inline Event */}
      <Section
        title="1️⃣ Inline Event Handling"
        code={`<button onclick="alert('Button clicked!')">Click Me</button>`}
        output={`Shows alert “Button clicked!” when button is clicked.`}
      />

      {/* Example 2: addEventListener */}
      <Section
        title="2️⃣ addEventListener()"
        code={`<button id="btn">Click Me</button>

<script>
document.getElementById("btn").addEventListener("click", () => {
  alert("Button was clicked!");
});
</script>`}
        output={`Displays alert “Button was clicked!” when button is pressed.`}
      />

      {/* Example 3: Mouse Events */}
      <Section
        title="3️⃣ Mouse Events"
        code={`<div id="box" style="width:150px;height:150px;background:lightgray;line-height:150px;text-align:center;">
Hover Me
</div>

<script>
const box = document.getElementById("box");
box.addEventListener("mouseover", () => box.style.background = "lightgreen");
box.addEventListener("mouseout", () => box.style.background = "lightgray");
box.addEventListener("dblclick", () => alert("Double Clicked!"));
</script>`}
        output={`Hover changes color, double-click shows alert.`}
      />

      {/* Example 4: Keyboard Events */}
      <Section
        title="4️⃣ Keyboard Events"
        code={`<p>Press any key and check the console.</p>
<script>
document.addEventListener("keydown", e => {
  console.log("Key pressed:", e.key);
});
</script>`}
        output={`Logs pressed keys in the browser console.`}
      />

      {/* Example 5: Form Submit */}
      <Section
        title="5️⃣ Form Events (Submit)"
        code={`<form id="myForm">
  <input type="text" id="name" placeholder="Enter your name">
  <button type="submit">Submit</button>
</form>

<script>
document.getElementById("myForm").addEventListener("submit", event => {
  event.preventDefault();
  const name = document.getElementById("name").value;
  alert("Form submitted! Name: " + name);
});
</script>`}
        output={`Shows alert with entered name when form submitted.`}
      />

      {/* Example 6: Input & Change Events */}
      <Section
        title="6️⃣ Input & Change Events"
        code={`<input id="textInput" placeholder="Type something..." style="padding:8px;width:200px;">
<p id="output"></p>

<script>
const input = document.getElementById("textInput");
const output = document.getElementById("output");

input.addEventListener("input", () => {
  output.textContent = "You typed: " + input.value;
});

input.addEventListener("change", () => {
  alert("Input changed to: " + input.value);
});
</script>`}
        output={`Shows live text as you type, and alert when you leave input.`}
      />

      {/* Example 7: Focus & Blur */}
      <Section
        title="7️⃣ Focus & Blur Events"
        code={`<input id="focusBox" placeholder="Click here" style="padding:8px;">
<script>
const box = document.getElementById("focusBox");
box.addEventListener("focus", () => box.style.backgroundColor = "#bbf7d0");
box.addEventListener("blur", () => box.style.backgroundColor = "#fff");
</script>`}
        output={`Box highlights when focused and resets when blurred.`}
      />

      {/* Example 8: Window Resize */}
      <Section
        title="8️⃣ Window Resize Event"
        code={`<p id="sizeInfo">Resize the window!</p>
<script>
window.addEventListener("resize", () => {
  document.getElementById("sizeInfo").textContent =
    "Width: " + window.innerWidth + ", Height: " + window.innerHeight;
});
</script>`}
        output={`Displays live window size on resize.`}
      />

      {/* Example 9: Scroll Event */}
      <Section
        title="9️⃣ Scroll Event"
        code={`<div style="height:1500px;background:linear-gradient(white,lightblue);">
  <p id="scrollText" style="position:fixed;top:10px;left:10px;">Scroll down!</p>
</div>

<script>
window.addEventListener("scroll", () => {
  document.getElementById("scrollText").textContent =
    "Scroll position: " + window.scrollY + "px";
});
</script>`}
        output={`Shows current scroll position while scrolling.`}
      />

      {/* Example 10: Load Event */}
      <Section
        title="🔟 Window Load Event"
        code={`<script>
window.addEventListener("load", () => {
  alert("Page fully loaded!");
});
</script>`}
        output={`Shows alert once page has finished loading.`}
      />

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>event bubbling</b> and <b>delegation</b>.
          </li>
          <li>
            Experiment with <b>custom events</b> and advanced patterns.
          </li>
          <li>Build mini-projects (like form validation or games).</li>
        </ul>
      </div>
    </div>
  );
}

// 🔹 Reusable Section Component
function Section({ title, code, output }) {
  return (
    <div className="tutorial-section">
      <h2 className="section-title">{title}</h2>
      <div className="code-demo-box">
        <CodeCopyBlock code={code} />
        <TryItButton code={code} />
      </div>
      <h3 style={{ marginTop: "1rem" }}>Output:</h3>
      <div
        style={{
          fontSize: "1.1rem",
          padding: "10px",
          border: "2px dashed #2563eb",
          borderRadius: "8px",
        }}
      >
        <pre>{output}</pre>
      </div>
    </div>
  );
}
